import { ReactNode } from 'react'
import { Box, Table, Text, TextField } from '@radix-ui/themes'
import { PluginJsonSchema, PluginJsonSchemaProperty } from './plugin-schema-types'
import { formatValueForDisplay, resolveEffectiveType } from './plugin-config-coercion'

function isArrayType(schema: PluginJsonSchemaProperty): boolean {
  return resolveEffectiveType(schema) === 'array'
}

function isObjectType(schema: PluginJsonSchemaProperty): boolean {
  return resolveEffectiveType(schema) === 'object' && !!schema.properties
}

type PluginConfigEditorProps = {
  schema: PluginJsonSchema
  values: Record<string, unknown>
  onChange: (values: Record<string, unknown>) => void
  errors?: Record<string, string>
}

/** Immutably sets a value at a dot-path (e.g. ["barcodeLengths", "draw"]) within a nested record. */
function setAtPath(
  values: Record<string, unknown>,
  path: string[],
  value: string,
): Record<string, unknown> {
  const [head, ...rest] = path
  if (rest.length === 0) {
    return { ...values, [head]: value }
  }
  const child = (values[head] as Record<string, unknown>) ?? {}
  return { ...values, [head]: setAtPath(child, rest, value) }
}

export const PluginConfigEditor = ({
  schema,
  values,
  onChange,
  errors,
}: PluginConfigEditorProps) => {
  if (!schema.properties) {
    return null
  }

  const handleFieldChange = (path: string[], value: string) => {
    onChange(setAtPath(values, path, value))
  }

  // Renders the rows for one object level. `path` is the key path to this level; `indent` nests object groups visually.
  const renderProperties = (
    properties: PluginJsonSchema['properties'],
    pathValues: Record<string, unknown>,
    path: string[],
    indent: number,
    requiredFields: string[],
  ): ReactNode[] => {
    if (!properties) {
      return []
    }
    return Object.entries(properties).map(([key, propertyDef]) => {
      const propertySchema = typeof propertyDef === 'boolean' ? undefined : propertyDef
      const isRequired = requiredFields.includes(key)
      const label = propertySchema?.description || key
      const fieldPath = [...path, key]
      const pathKey = fieldPath.join('.')

      if (propertySchema && isObjectType(propertySchema)) {
        const childValues = (pathValues[key] as Record<string, unknown>) ?? {}
        return [
          <Table.Row key={pathKey}>
            <Table.Cell style={{ paddingLeft: `${indent * 16}px` }}>
              <Text weight="medium">{label}</Text>
            </Table.Cell>
            <Table.Cell />
          </Table.Row>,
          ...renderProperties(
            propertySchema.properties,
            childValues,
            fieldPath,
            indent + 1,
            propertySchema.required ?? [],
          ),
        ]
      }

      return (
        <Table.Row key={pathKey}>
          <Table.Cell style={{ paddingLeft: `${indent * 16}px` }}>
            {label}
            {isRequired ? <span className="plugin-config-required"> *</span> : ''}
          </Table.Cell>
          <Table.Cell>
            <Box>
              <TextField.Root
                value={formatValueForDisplay(pathValues[key])}
                onChange={(e) => handleFieldChange(fieldPath, e.target.value)}
                placeholder={label}
                color={errors?.[pathKey] ? 'red' : undefined}
              />
              {propertySchema && isArrayType(propertySchema) && (
                <Text size="1" color="gray">
                  Comma-separated values
                </Text>
              )}
              {errors?.[pathKey] && (
                <Text size="1" color="red">
                  {errors[pathKey]}
                </Text>
              )}
            </Box>
          </Table.Cell>
        </Table.Row>
      )
    })
  }

  return (
    <Table.Root style={{ tableLayout: 'fixed' }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell style={{ width: '30%' }}>Key</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderProperties(schema.properties, values, [], 0, schema.required ?? [])}
      </Table.Body>
    </Table.Root>
  )
}
